import { useEffect, useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Area,
    AreaChart,
} from "recharts";
import { Eye, Users, Briefcase, DollarSign, TrendingUp, Mail, MailWarning } from "lucide-react";
import { get } from "@/lib/api";
import { toast } from "sonner";

const fmtMoney = (v) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(v || 0);

const STAT_CARDS = [
    {
        key: "views_total",
        label: "Page Views",
        icon: Eye,
        sub: (s) => `${s.views_7d} in last 7d`,
        format: (v) => v.toLocaleString(),
    },
    {
        key: "prospects_total",
        label: "Prospects",
        icon: Users,
        sub: (s) => `${s.prospects_pending} pending · ${s.prospects_7d} this week`,
        format: (v) => v.toLocaleString(),
    },
    {
        key: "clients_active",
        label: "Active Clients",
        icon: Briefcase,
        sub: (s) => `${s.conversion_rate_pct}% conversion`,
        format: (v) => v.toLocaleString(),
    },
    {
        key: "mrr_active_usd",
        label: "Active Plan Value",
        icon: TrendingUp,
        sub: (s) => `${fmtMoney(s.revenue_30d_usd)} last 30d`,
        format: fmtMoney,
    },
    {
        key: "revenue_total_usd",
        label: "Total Revenue",
        icon: DollarSign,
        sub: () => `Lifetime payments`,
        format: fmtMoney,
    },
];

export default function Overview() {
    const [stats, setStats] = useState(null);
    const [daily, setDaily] = useState([]);
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [s, d, e] = await Promise.all([
                    get("/admin/stats"),
                    get("/admin/views/daily?days=14"),
                    get("/admin/system/email_status"),
                ]);
                setStats(s);
                setDaily(d);
                setEmail(e);
            } catch (err) {
                toast.error("Failed to load admin overview");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div data-testid="admin-overview">
            <header className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                    Overview
                </p>
                <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                    Command Center
                </h1>
                <p className="mt-2 text-white/55 text-sm">
                    Real-time view of leads, clients, and revenue.
                </p>
            </header>

            {email && (
                <div
                    data-testid="email-status-banner"
                    className={`mb-8 px-4 py-3 border ${
                        email.configured
                            ? "border-[#1E90FF]/40 bg-[#0a1429] text-[#a3b5d1]"
                            : "border-[#D4AF37]/40 bg-[#1a1503] text-[#fde68a]"
                    } flex items-start gap-3 text-xs`}
                >
                    {email.configured ? (
                        <Mail size={16} className="mt-0.5 text-[#00B7FF]" />
                    ) : (
                        <MailWarning size={16} className="mt-0.5 text-[#D4AF37]" />
                    )}
                    <div>
                        <p className="font-bold uppercase tracking-[0.18em]">
                            Email Service{" "}
                            {email.configured ? "Live" : "Not Configured"}
                        </p>
                        <p className="mt-1 leading-relaxed">
                            {email.configured ? (
                                <>
                                    Booking confirmations + admin notifications
                                    are being sent through Resend. Sender:{" "}
                                    <code className="text-white">
                                        {email.sender}
                                    </code>
                                    .
                                </>
                            ) : (
                                <>
                                    Add a <code className="text-white">RESEND_API_KEY</code>{" "}
                                    to <code className="text-white">backend/.env</code> and
                                    restart the backend to enable booking emails.
                                </>
                            )}
                        </p>
                    </div>
                </div>
            )}

            <section
                data-testid="stat-cards"
                className="grid grid-cols-2 lg:grid-cols-5 gap-3"
            >
                {STAT_CARDS.map(({ key, label, icon: Icon, sub, format }) => (
                    <div
                        key={key}
                        data-testid={`stat-${key}`}
                        className="bg-[#060c1e] border border-[#1a2a4a] p-5 hover:border-[#1E90FF]/50 transition-colors"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/55">
                                {label}
                            </p>
                            <Icon size={16} className="text-[#00B7FF]" />
                        </div>
                        <p className="font-display font-black text-white text-3xl tracking-tighter">
                            {loading || !stats ? "—" : format(stats[key] || 0)}
                        </p>
                        <p className="mt-1 text-[11px] text-white/45">
                            {loading || !stats ? " " : sub(stats)}
                        </p>
                    </div>
                ))}
            </section>

            <section className="mt-8 bg-[#060c1e] border border-[#1a2a4a] p-5 md:p-7">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                            Traffic
                        </p>
                        <h2 className="mt-1 font-display font-bold text-white text-xl">
                            Page views · last 14 days
                        </h2>
                    </div>
                </div>
                <div className="h-64" data-testid="views-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={daily}>
                            <defs>
                                <linearGradient id="vfill" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="0%"
                                        stopColor="#1E90FF"
                                        stopOpacity={0.6}
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor="#1E90FF"
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#142142" strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                stroke="#6b7c97"
                                fontSize={11}
                                tickFormatter={(s) => s.slice(5)}
                            />
                            <YAxis
                                stroke="#6b7c97"
                                fontSize={11}
                                allowDecimals={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#020617",
                                    border: "1px solid #1a2a4a",
                                    color: "#fff",
                                    fontSize: 12,
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="views"
                                stroke="#00B7FF"
                                strokeWidth={2}
                                fill="url(#vfill)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </section>
        </div>
    );
}
